import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Users, Eye, TrendingUp, Clock, Globe, Monitor, Smartphone, Tablet,
  Download, FileSpreadsheet, Loader2, Plus, X, RefreshCw, Radio,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import api, { BACKEND } from "@/lib/api";
import { Header } from "./Schools";

const COLORS = ["#4F46E5", "#0891B2", "#16A34A", "#D97706", "#DC2626", "#7C3AED"];
const SI = "px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm w-full focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 bg-white";

function StatCard({ icon: Icon, label, value, sub, color = "#4F46E5" }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: `${color}15` }}>
          <Icon className="w-4.5 h-4.5" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value ?? "—"}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function fmtSeconds(s) {
  if (!s) return "0s";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

export default function VisitorAnalytics() {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(null);
  const [tab, setTab] = useState("overview");
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [newIp, setNewIp] = useState("");
  const [exporting, setExporting] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, chartRes, settingsRes] = await Promise.all([
        api.get("/admin/visitors/stats"),
        api.get(`/admin/visitors/chart-data?range=${range}`),
        api.get("/admin/visitors/settings"),
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data);
      setSettings(settingsRes.data);
    } catch { toast.error("Failed to load visitor analytics"); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  // Live online counter — polls every 30s, only when live counter enabled
  useEffect(() => {
    if (!settings?.live_counter_enabled) return;
    const poll = () => api.get("/admin/visitors/online").then(r => setOnline(r.data.online)).catch(() => {});
    poll();
    const intervalMs = (settings?.refresh_interval_seconds ?? 30) * 1000;
    const interval = setInterval(poll, intervalMs);
    return () => clearInterval(interval);
  }, [settings?.live_counter_enabled, settings?.refresh_interval_seconds]);

  async function saveSettings() {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/visitors/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingSettings(false); }
  }

  function addIp() {
    const ip = newIp.trim();
    if (!ip) return;
    if (settings.excluded_ips.includes(ip)) { toast.error("Already excluded"); return; }
    setSettings(s => ({ ...s, excluded_ips: [...s.excluded_ips, ip] }));
    setNewIp("");
  }

  function removeIp(ip) {
    setSettings(s => ({ ...s, excluded_ips: s.excluded_ips.filter(x => x !== ip) }));
  }

  async function clearData() {
    if (!window.confirm("Permanently delete all visitor analytics data? This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/visitors/clear");
      toast.success(`Cleared ${data.deleted} records`);
      load();
    } catch { toast.error("Clear failed"); }
  }

  async function exportFile(fmt) {
    setExporting(fmt);
    try {
      const token = localStorage.getItem("token") || "";
      const url = `${BACKEND}/api/admin/visitors/export?format=${fmt}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      link.download = `visitor-analytics-${stamp}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error(`Export ${fmt.toUpperCase()} failed`); }
    finally { setExporting(""); }
  }

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "charts", label: "Charts" },
    { id: "settings", label: "Settings" },
  ];

  if (loading && !stats) {
    return (
      <div className="fade-up">
        <Header title="Visitor Analytics" subtitle="Website traffic and engagement, tracked anonymously." />
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(n => <div key={n} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up pb-12">
      <Header
        title="Visitor Analytics"
        subtitle="Website traffic and engagement, tracked anonymously — no cookies, no personal data."
        actions={
          <div className="flex items-center gap-2">
            {settings?.live_counter_enabled && online !== null && (
              <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                <Radio className="w-3 h-3 animate-pulse" /> {online} online
              </div>
            )}
            <button onClick={load} className="inline-flex items-center gap-1.5 border border-gray-200 bg-white text-gray-700 text-sm px-3 py-2 rounded-lg hover:bg-gray-50 transition">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        }
      />

      <div className="flex gap-0 mt-6 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === "overview" && stats && (
        <div className="mt-6 space-y-6 max-w-6xl">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard icon={Eye} label="Today" value={stats.today} color="#4F46E5" />
            <StatCard icon={Eye} label="Yesterday" value={stats.yesterday} color="#0891B2" />
            <StatCard icon={TrendingUp} label="This Week" value={stats.this_week} color="#16A34A" />
            <StatCard icon={TrendingUp} label="This Month" value={stats.this_month} color="#D97706" />
            <StatCard icon={TrendingUp} label="This Year" value={stats.this_year} color="#9333EA" />
            <StatCard icon={Users} label="Total Visitors" value={stats.total} color="#7C3AED" />
            <StatCard icon={Radio} label="Online Now" value={stats.online} color="#DC2626" sub="last 5 min" />
            <StatCard icon={Users} label="Unique Visitors" value={stats.unique_visitors} color="#0F172A" />
            <StatCard icon={Clock} label="Avg. Session" value={fmtSeconds(stats.avg_session_seconds)} color="#65A30D" />
            <StatCard icon={TrendingUp} label="Bounce Rate" value={`${stats.bounce_rate ?? 0}%`} color="#DB2777" sub="single-page visits" />
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-sm font-semibold text-gray-700 mb-3">Top Pages</div>
              {stats.top_pages.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {stats.top_pages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate">{p.page}</span>
                      <span className="text-gray-900 font-medium shrink-0 ml-2">{p.visits}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-sm font-semibold text-gray-700 mb-3">Top Referrers</div>
              {(stats.top_referrers || []).length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {stats.top_referrers.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate">{r.referrer}</span>
                      <span className="text-gray-900 font-medium shrink-0 ml-2">{r.visits}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-sm font-semibold text-gray-700 mb-3">Returning vs New</div>
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.returning_visitors}</div>
                  <div className="text-xs text-gray-500">Returning</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.unique_visitors - stats.returning_visitors}</div>
                  <div className="text-xs text-gray-500">New</div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Recent Visits</span>
              <div className="flex gap-2">
                <button onClick={() => exportFile("csv")} disabled={!!exporting}
                  className="inline-flex items-center gap-1 text-xs border border-gray-200 bg-white px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
                  {exporting === "csv" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} CSV
                </button>
                <button onClick={() => exportFile("xlsx")} disabled={!!exporting}
                  className="inline-flex items-center gap-1 text-xs border border-gray-200 bg-white px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
                  {exporting === "xlsx" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />} Excel
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                    <th className="px-5 py-2.5 font-medium">Page</th>
                    <th className="px-5 py-2.5 font-medium">Device</th>
                    <th className="px-5 py-2.5 font-medium">Browser</th>
                    <th className="px-5 py-2.5 font-medium">Referrer</th>
                    <th className="px-5 py-2.5 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {stats.recent_visits.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400 text-xs">No visits recorded yet</td></tr>
                  ) : stats.recent_visits.map((v, i) => (
                    <tr key={i} className="hover:bg-gray-50/50">
                      <td className="px-5 py-2.5 text-gray-700 truncate max-w-[160px]">{v.landing_page}</td>
                      <td className="px-5 py-2.5 text-gray-500 capitalize">{v.device}</td>
                      <td className="px-5 py-2.5 text-gray-500">{v.browser}</td>
                      <td className="px-5 py-2.5 text-gray-500 truncate max-w-[140px]">{v.referrer || "Direct"}</td>
                      <td className="px-5 py-2.5 text-gray-400 text-xs whitespace-nowrap">{new Date(v.visit_time).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── CHARTS ── */}
      {tab === "charts" && chartData && (
        <div className="mt-6 space-y-6 max-w-6xl">
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-700">Visitors Trend</span>
              <div className="flex gap-2">
                {["7d", "30d"].map(r => (
                  <button key={r} onClick={() => setRange(r)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${range === r ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                    Last {r === "7d" ? "7" : "30"} Days
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData.daily} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="visitors" stroke="#4F46E5" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <Monitor className="w-3.5 h-3.5" /> Device Distribution
              </div>
              {chartData.devices.length === 0 ? (
                <p className="text-xs text-gray-400 py-10 text-center">No data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={chartData.devices} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {chartData.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Browser Distribution
              </div>
              {chartData.browsers.length === 0 ? (
                <p className="text-xs text-gray-400 py-10 text-center">No data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData.browsers} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#0891B2" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <Smartphone className="w-3.5 h-3.5" /> OS Distribution
              </div>
              {(chartData.os || []).length === 0 ? (
                <p className="text-xs text-gray-400 py-10 text-center">No data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData.os} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#7C3AED" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS ── */}
      {tab === "settings" && settings && (
        <div className="mt-6 space-y-5 max-w-2xl">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
            <div className="text-sm font-semibold text-gray-700">General</div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={settings.enabled} onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))} className="accent-indigo-600 w-4 h-4" />
              Enable Analytics
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={settings.live_counter_enabled} onChange={e => setSettings(s => ({ ...s, live_counter_enabled: e.target.checked }))} className="accent-indigo-600 w-4 h-4" />
              Enable Live Counter
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={settings.exclude_bots} onChange={e => setSettings(s => ({ ...s, exclude_bots: e.target.checked }))} className="accent-indigo-600 w-4 h-4" />
              Exclude Bots &amp; Crawlers
            </label>
            <div>
              <label className="text-xs font-semibold text-gray-700">Unique Visit Timeout (minutes)</label>
              <input type="number" min={1} max={1440} value={settings.unique_visit_timeout_minutes}
                onChange={e => setSettings(s => ({ ...s, unique_visit_timeout_minutes: parseInt(e.target.value) || 30 }))}
                className={SI + " mt-1"} />
              <p className="text-[10px] text-gray-400 mt-1">A returning visit within this window won't count as a new unique visitor.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Live Counter Refresh Interval (seconds)</label>
              <input type="number" min={5} max={300} value={settings.refresh_interval_seconds ?? 30}
                onChange={e => setSettings(s => ({ ...s, refresh_interval_seconds: parseInt(e.target.value) || 30 }))}
                className={SI + " mt-1"} />
              <p className="text-[10px] text-gray-400 mt-1">How often the "online now" counter polls for updates.</p>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3">
            <div className="text-sm font-semibold text-gray-700">Excluded IP Addresses</div>
            <p className="text-xs text-gray-400">Visits from these IPs (e.g. admin/office) are never tracked.</p>
            {settings.request_ip && !settings.excluded_ips.includes(settings.request_ip) && (
              <button
                onClick={() => setSettings(s => ({ ...s, excluded_ips: [...s.excluded_ips, settings.request_ip] }))}
                className="text-xs inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <Plus className="w-3 h-3" /> Exclude my current IP ({settings.request_ip})
              </button>
            )}
            <div className="space-y-1.5">
              {settings.excluded_ips.map(ip => (
                <div key={ip} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-sm font-mono text-gray-700">{ip}</span>
                  <button onClick={() => removeIp(ip)} className="text-rose-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {settings.excluded_ips.length === 0 && <p className="text-xs text-gray-400 py-2">No excluded IPs yet.</p>}
            </div>
            <div className="flex gap-2">
              <input className={SI} placeholder="e.g. 127.0.0.1" value={newIp} onChange={e => setNewIp(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addIp())} />
              <button onClick={addIp} className="shrink-0 inline-flex items-center gap-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-rose-100 bg-rose-50/40 p-5 space-y-3">
            <div className="text-sm font-semibold text-rose-700">Danger Zone</div>
            <p className="text-xs text-rose-600/80">Permanently delete all recorded visitor data. This cannot be undone.</p>
            <button onClick={clearData} className="text-sm border border-rose-200 bg-white text-rose-600 px-4 py-2 rounded-lg hover:bg-rose-50 transition">
              Clear Analytics Data
            </button>
          </div>

          <button onClick={saveSettings} disabled={savingSettings}
            className="inline-flex items-center gap-2 bg-[#0F172A] text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-60">
            {savingSettings ? "Saving…" : "Save Settings"}
          </button>
        </div>
      )}
    </div>
  );
}
